import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/RegisterPartner.css';
import sociasApiService from '../services/sociasApiService';
import { getTiposSocia } from '../services/tiposSociaService';
import profesionService from '../services/profesionService';
import mentorService from '../services/mentorService';
import locationService from '../services/locationService';

const initialData = {
  // Datos personales
  cedula: '', tipoSocio: '', apellidos: '', nombres: '', fechaNacimiento: '', edad: '', sexo: '', profesion: '', nacionalidad: '', estadoCivil: '', foto: null,
  // Ubicación
  pais: '', ciudadDistrito: '', sectorBarrio: '', provinciaRegion: '', correoElectronico: '', telefono1: '', telefono2: '', direccion: '',
  // Redes sociales
  facebook: '', instagram: '', twitter: '',
  // Médicos
  tipoSindrome: '', discapacidad: '', tipoDiscapacidad: '', iess: '', talla: '', ministerioSalud: '', estado: '', fechaInscripcion: '', cariotipo: '', porcentaje: '', grado: '', edadDeteccion: '', hormonaCrecimiento: '', mentor: '',
  // Representante (solo si menor de edad)
  representanteCedula: '', representanteApellidos: '', representanteNombres: ''
};

export default function EditPartner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialData);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState('personales');
  const [errors, setErrors] = useState({});
  
  // Estados para dropdowns
  const [tiposSocio, setTiposSocio] = useState([]);
  const [profesiones, setProfesiones] = useState([]);
  const [mentores, setMentores] = useState([]);
  const [paises, setPaises] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [loadingPaises, setLoadingPaises] = useState(false);
  const [loadingTiposSocio, setLoadingTiposSocio] = useState(false);
  const [loadingProfesiones, setLoadingProfesiones] = useState(false);
  const [loadingMentores, setLoadingMentores] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedProvinciaCode, setSelectedProvinciaCode] = useState('');
  const [isMentor, setIsMentor] = useState(false); // Para saber si la socia es mentora
  const ALL_GRADOS = ['Leve o ligera', 'Moderada', 'Grave', 'Muy grave o severa', 'Discapacidad completa'];
  const [allowedGrados, setAllowedGrados] = useState(ALL_GRADOS);

  const computeAllowedGrados = (percent) => {
    if (percent === '' || percent === null || percent === undefined) return ALL_GRADOS;
    const n = Number(percent);
    if (isNaN(n)) return ALL_GRADOS;
    if (n === 100) return ['Discapacidad completa'];
    if (n >= 75) return ['Muy grave o severa'];
    if (n >= 60) return ['Grave'];
    if (n >= 40) return ['Moderada'];
    if (n >= 0) return ['Leve o ligera'];
    return ALL_GRADOS;
  };

  const handleAccordion = (section) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  // Cargar datos de la socia desde la API
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        setLoadingTiposSocio(true);
        setLoadingProfesiones(true);
        setLoadingMentores(true);
        setLoadingPaises(true);

        // Cargar tipos de socia
        const tiposRes = await getTiposSocia();
        console.log('👥 Tipos de socia cargados:', tiposRes.data?.length, tiposRes.data);
        setTiposSocio(tiposRes.data || []);
        setLoadingTiposSocio(false);

        // Cargar profesiones activas
        const profData = await profesionService.getAllProfesiones();
        const profesionesActivas = profData.filter(p => p.PROFESION_ESTADO === 'Activo');
        console.log('💼 Profesiones activas cargadas:', profesionesActivas.length, profesionesActivas);
        setProfesiones(profesionesActivas);
        setLoadingProfesiones(false);

        // Cargar mentores activos
        const mentoresRes = await mentorService.getAllMentores();
        const mentoresData = mentoresRes.data || mentoresRes;
        setMentores(Array.isArray(mentoresData) ? mentoresData.filter(m => m.mentorEstado === 'Activo') : []);
        setLoadingMentores(false);

        // Cargar países
        const paisesData = await locationService.getCountries();
        console.log('🌎 Países cargados:', paisesData?.length, paisesData);
        setPaises(paisesData);
        setLoadingPaises(false);
        
        return paisesData;

      } catch (error) {
        console.error('Error al cargar dropdowns:', error);
        setLoadingTiposSocio(false);
        setLoadingProfesiones(false);
        setLoadingMentores(false);
        setLoadingPaises(false);
        return [];
      }
    };

    const loadSociaData = async (paisesData) => {
      try {
        setLoading(true);
        console.log('🔍 Cargando datos de socia con ID:', id);
        
        const sociaData = await sociasApiService.getSociaById(id);
        console.log('📋 Datos de socia recibidos:', sociaData);
        
        // Función auxiliar para formatear fechas ISO a YYYY-MM-DD
        const formatDateForInput = (isoDate) => {
          if (!isoDate) return '';
          try {
            const date = new Date(isoDate);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          } catch (error) {
            console.error('Error al formatear fecha:', isoDate, error);
            return '';
          }
        };

        // Función auxiliar para valores que pueden ser null, undefined, 0, o string vacío
        const safeValue = (value, defaultValue = '') => {
          if (value === null || value === undefined) return defaultValue;
          return String(value);
        };
        
        // Mapear datos de la API al formato del formulario (flat structure)
        console.log('🔍 Valores clave de la socia:');
        console.log('  - ts (tipoSociaId):', sociaData.ts, typeof sociaData.ts);
        console.log('  - profesionId:', sociaData.profesionId, typeof sociaData.profesionId);
        console.log('  - sociaSexo:', sociaData.sociaSexo, typeof sociaData.sociaSexo);
        console.log('  - sociaPais:', sociaData.sociaPais, typeof sociaData.sociaPais);
        console.log('  - sociaProvincia:', sociaData.sociaProvincia, typeof sociaData.sociaProvincia);
        console.log('  - sociaCiudad:', sociaData.sociaCiudad, typeof sociaData.sociaCiudad);
        
        // Normalizar porcentaje para edición (mostrar siempre 0-100)
        const normalizePercentDisplay = (val) => {
          if (val === null || val === undefined || val === '') return '';
          const num = Number(val);
          if (isNaN(num)) return '';
          let n = num;
          if (n > 0 && n <= 1) n = n * 100;
          if (n < 0) n = 0;
          if (n > 100) n = 100;
          return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2)));
        };

        const mappedData = {
          cedula: safeValue(sociaData.sociaCedula),
          tipoSocio: safeValue(sociaData.ts),
          apellidos: safeValue(sociaData.sociaApellido),
          nombres: safeValue(sociaData.sociaNombre),
          fechaNacimiento: formatDateForInput(sociaData.sociaFechaNacimiento),
          edad: safeValue(sociaData.sociaEdad),
          sexo: safeValue(sociaData.sociaSexo),
          profesion: safeValue(sociaData.profesionId),
          nacionalidad: safeValue(sociaData.sociaNacionalidad),
          estadoCivil: safeValue(sociaData.sociaEstadoCivil),
          foto: null, // El input file siempre vacío, pero mostramos preview
          pais: safeValue(sociaData.sociaPais),
          ciudadDistrito: safeValue(sociaData.sociaCiudad),
          sectorBarrio: safeValue(sociaData.sociaSector),
          provinciaRegion: safeValue(sociaData.sociaProvincia),
          correoElectronico: safeValue(sociaData.sociaEmail),
          telefono1: safeValue(sociaData.sociaTelefono1),
          telefono2: safeValue(sociaData.sociaTelefono2),
          direccion: safeValue(sociaData.sociaDireccion),
          facebook: safeValue(sociaData.sociaRfacebook),
          instagram: safeValue(sociaData.sociaRinstagram),
          twitter: safeValue(sociaData.sociaRx),
          tipoSindrome: safeValue(sociaData.sociaTipoSindrome),
          discapacidad: sociaData.sociaCarnetDiscapacidad === 1 ? 'Si' : sociaData.sociaCarnetDiscapacidad === 0 ? 'No' : '',
          tipoDiscapacidad: safeValue(sociaData.sociaTipoDiscapacidad),
          iess: sociaData.sociaIees === 1 ? 'Si' : sociaData.sociaIees === 0 ? 'No' : '',
          talla: safeValue(sociaData.sociaTalla),
          ministerioSalud: sociaData.sociaMsp === 1 ? 'Si' : sociaData.sociaMsp === 0 ? 'No' : '',
          estado: safeValue(sociaData.sociaEstado),
          fechaInscripcion: formatDateForInput(sociaData.sociaFechaInscripcion),
          cariotipo: safeValue(sociaData.sociaCariotipo),
          porcentaje: normalizePercentDisplay(sociaData.sociaPorcentajeDiscapacidad),
          grado: safeValue(sociaData.sociaGrado),
          edadDeteccion: safeValue(sociaData.sociaEdadDeteccion),
          hormonaCrecimiento: sociaData.sociaHormonaCrecimiento === 1 ? 'Si' : sociaData.sociaHormonaCrecimiento === 0 ? 'No' : '',
          mentor: safeValue(sociaData.mentorId),
          representanteCedula: safeValue(sociaData.sociaRepresentanteCedula),
          representanteNombres: sociaData.sociaRepresentanteNombre ? sociaData.sociaRepresentanteNombre.split(' ').slice(1).join(' ') : '',
          representanteApellidos: sociaData.sociaRepresentanteNombre ? sociaData.sociaRepresentanteNombre.split(' ')[0] : ''
        };

        console.log('🔄 Datos mapeados para el formulario:', mappedData);
        setFormData(mappedData);
        // Inicializar grados permitidos según porcentaje cargado
        const initialAllowed = computeAllowedGrados(mappedData.porcentaje);
        setAllowedGrados(initialAllowed);
        if (mappedData.grado && !initialAllowed.includes(mappedData.grado)) {
          setFormData(prev => ({ ...prev, grado: initialAllowed[0] || '' }));
        }
        
        // Detectar si la socia es mentora (mentorDisplay contiene "Esta socia es mentor")
        const esMentora = sociaData.mentorDisplay && sociaData.mentorDisplay.includes('Esta socia es mentor');
        setIsMentor(esMentora);
        console.log('👩‍🏫 ¿Es mentora?', esMentora, '- mentorDisplay:', sociaData.mentorDisplay);
        
        // Vista previa de foto si existe (ahora viene de Cloudinary como URL)
        if (sociaData.sociaFotoUrl) {
          setFotoPreview(sociaData.sociaFotoUrl);
        }
        
        // Cargar provincias y ciudades si tiene país seleccionado
        if (sociaData.sociaPais && paisesData) {
          console.log('🌍 Cargando ubicaciones para:', sociaData.sociaPais);
          const countryData = paisesData.find(p => p.name === sociaData.sociaPais);
          
          if (countryData) {
            console.log('✅ País encontrado:', countryData);
            setSelectedCountryCode(countryData.isoCode);
            
            try {
              const provData = await locationService.getStates(countryData.isoCode);
              console.log('📍 Provincias cargadas:', provData.length);
              setProvincias(provData);
              
              // Cargar ciudades si tiene provincia seleccionada
              if (sociaData.sociaProvincia) {
                console.log('🏙️ Buscando provincia:', sociaData.sociaProvincia);
                const provinciaData = provData.find(p => p.name === sociaData.sociaProvincia);
                
                if (provinciaData) {
                  console.log('✅ Provincia encontrada:', provinciaData);
                  setSelectedProvinciaCode(provinciaData.isoCode);
                  
                  const ciudadesData = await locationService.getCities(countryData.isoCode, provinciaData.isoCode);
                  console.log('🏘️ Ciudades cargadas:', ciudadesData.length);
                  setCiudades(ciudadesData);
                } else {
                  console.warn('⚠️ Provincia no encontrada en la lista');
                }
              }
            } catch (error) {
              console.error('❌ Error al cargar ubicaciones:', error);
            }
          } else {
            console.warn('⚠️ País no encontrado en la lista');
          }
        }
        
      } catch (error) {
        console.error('❌ Error al cargar datos de la socia:', error);
        alert('Error al cargar los datos de la socia. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    const initialize = async () => {
      const paisesData = await loadDropdowns();
      if (id) {
        await loadSociaData(paisesData);
      } else {
        setLoading(false);
      }
    };

    initialize();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Normalización específica para porcentaje (si el usuario ingresa 0,6 o 0.6 lo interpretamos como 60)
    if (name === 'porcentaje') {
      const raw = (value ?? '').toString().replace(',', '.').trim();
      if (raw === '') {
        setFormData(prev => ({ ...prev, porcentaje: '' }));
        setAllowedGrados(ALL_GRADOS);
      } else {
        const num = Number(raw);
        if (isNaN(num)) {
          // Si no es número, no actualizamos el valor
          return;
        }
        let normalized = num;
        if (num > 0 && num <= 1) {
          normalized = num * 100;
        }
        // Limitar a rango razonable 0-100
        if (normalized < 0) normalized = 0;
        if (normalized > 100) normalized = 100;
        const asString = Number.isInteger(normalized) ? String(normalized) : String(parseFloat(normalized.toFixed(2)));
        setFormData(prev => ({ ...prev, porcentaje: asString }));
        const allowed = computeAllowedGrados(normalized);
        setAllowedGrados(allowed);
        // Si el grado actual no está permitido, ajustar al permitido por el porcentaje
        setFormData(prev => {
          if (prev.grado && !allowed.includes(prev.grado)) {
            return { ...prev, grado: allowed[0] || '' };
          }
          return prev;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Limpiar error del campo cuando el usuario escribe
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Calcular edad automáticamente cuando cambia la fecha de nacimiento
    if (name === 'fechaNacimiento' && value) {
      const hoy = new Date();
      const fechaNac = new Date(value);
      let edad = hoy.getFullYear() - fechaNac.getFullYear();
      const mes = hoy.getMonth() - fechaNac.getMonth();
      if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
        edad--;
      }
      setFormData(prev => ({ ...prev, edad: edad.toString() }));
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, foto: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaisChange = async (e) => {
    const paisNombre = e.target.value;
    console.log('🌍 Cambiando país a:', paisNombre);
    
    setFormData(prev => ({ 
      ...prev, 
      pais: paisNombre, 
      provinciaRegion: '', 
      ciudadDistrito: '' 
    }));
    setProvincias([]);
    setCiudades([]);
    
    if (paisNombre) {
      const pais = paises.find(p => p.name === paisNombre);
      console.log('🔍 País encontrado en lista:', pais);
      
      if (pais) {
        setSelectedCountryCode(pais.isoCode);
        try {
          console.log('📡 Cargando provincias para país:', pais.isoCode);
          const provData = await locationService.getStates(pais.isoCode);
          console.log('✅ Provincias cargadas:', provData.length, provData);
          setProvincias(provData);
        } catch (error) {
          console.error('❌ Error al cargar provincias:', error);
        }
      }
    }
  };

  const handleProvinciaChange = async (e) => {
    const provinciaNombre = e.target.value;
    console.log('📍 Cambiando provincia a:', provinciaNombre);
    
    setFormData(prev => ({ 
      ...prev, 
      provinciaRegion: provinciaNombre, 
      ciudadDistrito: '' 
    }));
    setCiudades([]);
    
    if (provinciaNombre && selectedCountryCode) {
      const provincia = provincias.find(p => p.name === provinciaNombre);
      console.log('🔍 Provincia encontrada en lista:', provincia);
      console.log('🌍 Country code actual:', selectedCountryCode);
      
      if (provincia) {
        setSelectedProvinciaCode(provincia.isoCode);
        try {
          console.log('📡 Cargando ciudades para:', selectedCountryCode, provincia.isoCode);
          const ciudadesData = await locationService.getCities(selectedCountryCode, provincia.isoCode);
          console.log('✅ Ciudades cargadas:', ciudadesData.length, ciudadesData);
          setCiudades(ciudadesData);
        } catch (error) {
          console.error('❌ Error al cargar ciudades:', error);
        }
      }
    }
  };

  const handleCancel = () => {
    navigate('/partners');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      console.log('💾 Guardando cambios de socia:', id);

      // Validación: el grado debe corresponder al porcentaje
      const allowed = computeAllowedGrados(formData.porcentaje);
      if (formData.grado && !allowed.includes(formData.grado)) {
        alert('El grado seleccionado no corresponde al porcentaje ingresado.');
        return;
      }
      
      // Crear FormData para enviar al backend (incluyendo posible foto nueva)
      const formDataToSend = new FormData();
      
      // Mapear campos del formulario a los nombres esperados por el backend
      formDataToSend.append('sociaApellido', formData.apellidos);
      formDataToSend.append('sociaNombre', formData.nombres);
      formDataToSend.append('sociaCedula', formData.cedula);
      formDataToSend.append('sociaFechaNacimiento', formData.fechaNacimiento);
      formDataToSend.append('sociaEdad', formData.edad);
      formDataToSend.append('sociaSexo', formData.sexo);
      formDataToSend.append('sociaNacionalidad', formData.nacionalidad);
      formDataToSend.append('sociaEstadoCivil', formData.estadoCivil);
      formDataToSend.append('tipoSociaId', formData.tipoSocio);
      formDataToSend.append('profesionId', formData.profesion);
      
      // Ubicación
      formDataToSend.append('sociaPais', formData.pais);
      formDataToSend.append('sociaProvincia', formData.provinciaRegion);
      formDataToSend.append('sociaCiudad', formData.ciudadDistrito);
      formDataToSend.append('sociaSector', formData.sectorBarrio);
      formDataToSend.append('sociaDireccion', formData.direccion);
      formDataToSend.append('sociaEmail', formData.correoElectronico);
      formDataToSend.append('sociaTelefono1', formData.telefono1);
      formDataToSend.append('sociaTelefono2', formData.telefono2 || '');
      
      // Redes sociales
      formDataToSend.append('sociaRfacebook', formData.facebook || '');
      formDataToSend.append('sociaRinstagram', formData.instagram || '');
      formDataToSend.append('sociaRx', formData.twitter || '');
      
      // Datos médicos
      formDataToSend.append('sociaTipoSindrome', formData.tipoSindrome || '');
      formDataToSend.append('sociaCariotipo', formData.cariotipo || '');
      formDataToSend.append('sociaCarnetDiscapacidad', formData.discapacidad === 'Si' ? 1 : 0);
      formDataToSend.append('sociaTipoDiscapacidad', formData.tipoDiscapacidad || '');
      // Normalizar porcentaje para envío (acepta 0-1 como fracción o 0-100 como porcentaje)
      (() => {
        const raw = (formData.porcentaje ?? '').toString().replace(',', '.').trim();
        let normalizedToSend = 0;
        if (raw !== '' && !isNaN(Number(raw))) {
          let num = Number(raw);
          if (num > 0 && num <= 1) num = num * 100;
          if (num < 0) num = 0;
          if (num > 100) num = 100;
          normalizedToSend = Number.isInteger(num) ? num : parseFloat(num.toFixed(2));
        }
        formDataToSend.append('sociaPorcentajeDiscapacidad', String(normalizedToSend));
      })();
      formDataToSend.append('sociaGrado', formData.grado || '');
      formDataToSend.append('sociaIees', formData.iess === 'Si' ? 1 : 0);
      formDataToSend.append('sociaEdadDeteccion', formData.edadDeteccion || 0);
      formDataToSend.append('sociaHormonaCrecimiento', formData.hormonaCrecimiento === 'Si' ? 1 : 0);
      formDataToSend.append('sociaMsp', formData.ministerioSalud === 'Si' ? 1 : 0);
      formDataToSend.append('sociaEstado', formData.estado);
      formDataToSend.append('sociaFechaInscripcion', formData.fechaInscripcion);
      formDataToSend.append('sociaTalla', formData.talla || '');
      
      // Representante
      formDataToSend.append('sociaRepresentante', parseInt(formData.edad) < 18 ? 1 : 0);
      formDataToSend.append('sociaRepresentanteCedula', formData.representanteCedula || '');
      formDataToSend.append('sociaRepresentanteNombre', 
        formData.representanteNombres && formData.representanteApellidos 
          ? `${formData.representanteApellidos} ${formData.representanteNombres}` 
          : ''
      );
      // Mentor
      // Si la socia es mentora (isMentor === true) mantenemos el valor actual.
      // Si no se seleccionó mentor, enviar la cadena 'No asignado' para indicar que no tiene mentor.
      const mentorValue = isMentor ? formData.mentor : (formData.mentor || 'No asignado');
      formDataToSend.append('mentorId', mentorValue);
      
      // Foto (solo si se seleccionó una nueva)
      if (formData.foto) {
        formDataToSend.append('sociaFoto', formData.foto);
      }

      await sociasApiService.updateSocia(id, formDataToSend);
      alert('✅ Cambios guardados exitosamente.');
      navigate('/partners');
    } catch (error) {
      console.error('❌ Error al guardar cambios:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.message || 'Error desconocido';
      alert('Error al guardar cambios: ' + errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin" />
        <p>Cargando datos de la socia...</p>
      </div>
    );
  }

  return (
    <div className="register-partner-container">
      <form onSubmit={handleSave} className="register-partner-form">
        {/* Sección Datos Personales */}
        <div className="accordion-section">
          <div
            className={`accordion-header${openSection==='personales' ? ' active' : ''}`}
            onClick={() => handleAccordion('personales')}
          >
            <span>Datos Personales</span>
            <span className="accordion-chevron">▶</span>
          </div>
          {openSection==='personales' && (
            <div className="accordion-content">
              <div className="form-row">
                <div className="form-group">
                  <label>Cédula (*)</label>
                  <input type="text" name="cedula" value={formData.cedula} onChange={handleInputChange} className={errors.cedula ? 'error' : ''} />
                  {errors.cedula && <span className="error-message">{errors.cedula}</span>}
                </div>
                <div className="form-group">
                  <label>Tipo de socio (*)</label>
                  <select name="tipoSocio" value={formData.tipoSocio} onChange={handleInputChange} className={errors.tipoSocio ? 'error' : ''}>
                    <option value="">Seleccione un tipo...</option>
                    {loadingTiposSocio ? (
                      <option disabled>Cargando tipos...</option>
                    ) : (
                      tiposSocio.map(tipo => (
                        <option key={tipo.TS} value={tipo.TS}>{tipo.TIPOSOCIA_NOMBRE}</option>
                      ))
                    )}
                  </select>
                  {errors.tipoSocio && <span className="error-message">{errors.tipoSocio}</span>}
                </div>
              </div>

              {/* Sección de representante si menor de edad */}
              {parseInt(formData.edad, 10) < 18 && (
                <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginTop: 16, background: '#fafafa' }}>
                  <div style={{ width: '100%', marginBottom: 8, fontWeight: 'bold', color: '#b2004c' }}>Datos de representante</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Cédula representante (*)</label>
                      <input type="text" name="representanteCedula" value={formData.representanteCedula} onChange={handleInputChange} className={errors.representanteCedula ? 'error' : ''} />
                      {errors.representanteCedula && <span className="error-message">{errors.representanteCedula}</span>}
                    </div>
                    <div className="form-group">
                      <label>Apellidos representante (*)</label>
                      <input type="text" name="representanteApellidos" value={formData.representanteApellidos} onChange={handleInputChange} className={errors.representanteApellidos ? 'error' : ''} />
                      {errors.representanteApellidos && <span className="error-message">{errors.representanteApellidos}</span>}
                    </div>
                    <div className="form-group">
                      <label>Nombres representante (*)</label>
                      <input type="text" name="representanteNombres" value={formData.representanteNombres} onChange={handleInputChange} className={errors.representanteNombres ? 'error' : ''} />
                      {errors.representanteNombres && <span className="error-message">{errors.representanteNombres}</span>}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Apellidos (*)</label>
                  <input type="text" name="apellidos" value={formData.apellidos} onChange={handleInputChange} className={errors.apellidos ? 'error' : ''} />
                  {errors.apellidos && <span className="error-message">{errors.apellidos}</span>}
                </div>
                <div className="form-group">
                  <label>Nombres (*)</label>
                  <input type="text" name="nombres" value={formData.nombres} onChange={handleInputChange} className={errors.nombres ? 'error' : ''} />
                  {errors.nombres && <span className="error-message">{errors.nombres}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fecha de nacimiento (*)</label>
                  <input type="date" name="fechaNacimiento" value={formData.fechaNacimiento} onChange={handleInputChange} className={errors.fechaNacimiento ? 'error' : ''} />
                  {errors.fechaNacimiento && <span className="error-message">{errors.fechaNacimiento}</span>}
                </div>
                <div className="form-group">
                  <label>EDAD (*)</label>
                  <input type="number" name="edad" value={formData.edad} readOnly className={errors.edad ? 'error' : ''} />
                  {errors.edad && <span className="error-message">{errors.edad}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Sexo (*)</label>
                  <select name="sexo" value={formData.sexo} onChange={handleInputChange} className={errors.sexo ? 'error' : ''}>
                    <option value="">Seleccione...</option>
                    <option value="F">Femenino</option>
                    <option value="M">Masculino</option>
                  </select>
                  {errors.sexo && <span className="error-message">{errors.sexo}</span>}
                </div>
                <div className="form-group">
                  <label>Profesión (*)</label>
                  <select name="profesion" value={formData.profesion} onChange={handleInputChange} className={errors.profesion ? 'error' : ''} disabled={loadingProfesiones}>
                    <option value="">Seleccione una profesión...</option>
                    {profesiones.map(prof => (
                      <option key={prof.PROFESION_ID} value={prof.PROFESION_ID}>
                        {prof.PROFESION_NOMBRE}
                      </option>
                    ))}
                  </select>
                  {loadingProfesiones && <span className="loading-message">Cargando profesiones...</span>}
                  {errors.profesion && <span className="error-message">{errors.profesion}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Nacionalidad (*)</label>
                  <input type="text" name="nacionalidad" value={formData.nacionalidad} onChange={handleInputChange} className={errors.nacionalidad ? 'error' : ''} />
                  {errors.nacionalidad && <span className="error-message">{errors.nacionalidad}</span>}
                </div>
                <div className="form-group">
                  <label>Estado Civil (*)</label>
                  <select name="estadoCivil" value={formData.estadoCivil} onChange={handleInputChange} className={errors.estadoCivil ? 'error' : ''}>
                    <option value="">Seleccione...</option>
                    <option value="Soltero/a">Soltero/a</option>
                    <option value="Casado/a">Casado/a</option>
                    <option value="Divorciado/a">Divorciado/a</option>
                    <option value="Viudo/a">Viudo/a</option>
                  </select>
                  {errors.estadoCivil && <span className="error-message">{errors.estadoCivil}</span>}
                </div>
              </div>
              <div className="form-row photo-section">
                <div className="form-group">
                  <label>FOTO (*)</label>
                  <div className="photo-upload-container">
                    <div className="photo-preview">
                      {fotoPreview ? (<img src={fotoPreview} alt="Vista previa" />) : (<div className="photo-placeholder"><p>Subir Fotografía tamaño carnet</p></div>)}
                    </div>
                    <input type="file" name="foto" onChange={handleFotoChange} accept="image/*" className={errors.foto ? 'error' : ''} />
                    {errors.foto && <span className="error-message">{errors.foto}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sección Ubicación */}
        <div className="accordion-section">
          <div
            className={`accordion-header${openSection==='ubicacion' ? ' active' : ''}`}
            onClick={() => handleAccordion('ubicacion')}
          >
            <span>Datos de Ubicación</span>
            <span className="accordion-chevron">▶</span>
          </div>
          {openSection==='ubicacion' && (
            <div className="accordion-content">
              <div className="form-row">
                <div className="form-group">
                  <label>País (*)</label>
                  <select 
                    name="pais" 
                    value={formData.pais} 
                    onChange={handlePaisChange} 
                    className={errors.pais ? 'error' : ''} 
                    disabled={loadingPaises}
                  >
                    <option value="">{loadingPaises ? 'Cargando países...' : 'Seleccione un país...'}</option>
                    {paises.map(pais => (
                      <option key={pais.isoCode} value={pais.name}>
                        {pais.name}
                      </option>
                    ))}
                  </select>
                  {errors.pais && <span className="error-message">{errors.pais}</span>}
                </div>
                <div className="form-group">
                  <label>Provincia/Región (*)</label>
                  <select 
                    name="provinciaRegion" 
                    value={formData.provinciaRegion} 
                    onChange={handleProvinciaChange} 
                    className={errors.provinciaRegion ? 'error' : ''} 
                    disabled={!formData.pais || provincias.length === 0}
                  >
                    <option value="">{!formData.pais ? 'Seleccione un país primero' : 'Seleccione una provincia...'}</option>
                    {provincias.map(provincia => (
                      <option key={provincia.isoCode} value={provincia.name}>
                        {provincia.name}
                      </option>
                    ))}
                  </select>
                  {errors.provinciaRegion && <span className="error-message">{errors.provinciaRegion}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Ciudad/Distrito (*)</label>
                  <select 
                    name="ciudadDistrito" 
                    value={formData.ciudadDistrito} 
                    onChange={handleInputChange} 
                    className={errors.ciudadDistrito ? 'error' : ''} 
                    disabled={!formData.provinciaRegion || ciudades.length === 0}
                  >
                    <option value="">{!formData.provinciaRegion ? 'Seleccione una provincia primero' : 'Seleccione una ciudad...'}</option>
                    {ciudades.map((ciudad, index) => (
                      <option key={index} value={ciudad}>
                        {ciudad}
                      </option>
                    ))}
                  </select>
                  {errors.ciudadDistrito && <span className="error-message">{errors.ciudadDistrito}</span>}
                </div>
                <div className="form-group">
                  <label>Sector/Barrio (*)</label>
                  <input type="text" name="sectorBarrio" value={formData.sectorBarrio} onChange={handleInputChange} className={errors.sectorBarrio ? 'error' : ''} />
                  {errors.sectorBarrio && <span className="error-message">{errors.sectorBarrio}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Correo Electrónico (*)</label>
                  <input type="email" name="correoElectronico" value={formData.correoElectronico} onChange={handleInputChange} className={errors.correoElectronico ? 'error' : ''} />
                  {errors.correoElectronico && <span className="error-message">{errors.correoElectronico}</span>}
                </div>
                <div className="form-group">
                  <label>Teléfono 1 (*)</label>
                  <input type="tel" name="telefono1" value={formData.telefono1} onChange={handleInputChange} className={errors.telefono1 ? 'error' : ''} placeholder="Ejemplo: 0999-999-999" />
                  {errors.telefono1 && <span className="error-message">{errors.telefono1}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Teléfono 2</label>
                  <input type="tel" name="telefono2" value={formData.telefono2} onChange={handleInputChange} className={errors.telefono2 ? 'error' : ''} placeholder="Ejemplo: 0999-999-999" />
                  {errors.telefono2 && <span className="error-message">{errors.telefono2}</span>}
                </div>
                <div className="form-group">
                  <label>Dirección (*)</label>
                  <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange} className={errors.direccion ? 'error' : ''} />
                  {errors.direccion && <span className="error-message">{errors.direccion}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Facebook</label>
                  <input type="text" name="facebook" value={formData.facebook} onChange={handleInputChange} placeholder="Usuario o enlace de Facebook" />
                </div>
                <div className="form-group">
                  <label>Instagram</label>
                  <input type="text" name="instagram" value={formData.instagram} onChange={handleInputChange} placeholder="Usuario o enlace de Instagram" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Twitter/X</label>
                  <input type="text" name="twitter" value={formData.twitter} onChange={handleInputChange} placeholder="Usuario o enlace de Twitter/X" />
                </div>
                <div className="form-group">
                  {/* Espacio vacío para mantener simetría */}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sección Médica */}
        <div className="accordion-section">
          <div
            className={`accordion-header${openSection==='medicos' ? ' active' : ''}`}
            onClick={() => handleAccordion('medicos')}
          >
            <span>Datos Médicos</span>
            <span className="accordion-chevron">▶</span>
          </div>
          {openSection==='medicos' && (
            <div className="accordion-content">
              <div className="form-row">
                <div className="form-group">
                  <label>Fecha de inscripción (*)</label>
                  <input type="date" name="fechaInscripcion" value={formData.fechaInscripcion} onChange={handleInputChange} className={errors.fechaInscripcion ? 'error' : ''} />
                  {errors.fechaInscripcion && <span className="error-message">{errors.fechaInscripcion}</span>}
                </div>
                <div className="form-group">
                  <label>Tipo de Síndrome (*)</label>
                  <select name="tipoSindrome" value={formData.tipoSindrome} onChange={handleInputChange} className={errors.tipoSindrome ? 'error' : ''}>
                    <option value="">Seleccione un tipo...</option>
                    <option value="Monosomía X completa (45,X)">Monosomía X completa (45,X)</option>
                    <option value="Mosaicismo 45,X/46,XX">Mosaicismo 45,X/46,XX</option>
                    <option value="Mosaicismo 45,X/47,XXX">Mosaicismo 45,X/47,XXX</option>
                    <option value="Mosaicismo 45,X/46,XY">Mosaicismo 45,X/46,XY</option>
                    <option value="Mosaicismo 45,X/46,X,i(Xq)">Mosaicismo 45,X/46,X,i(Xq)</option>
                    <option value="Mosaicismo 45,X/46,X,r(X)">Mosaicismo 45,X/46,X,r(X)</option>
                    <option value="Deleción del brazo corto del cromosoma">Deleción del brazo corto del cromosoma</option>
                    <option value="Deleción del brazo largo del cromosoma">Deleción del brazo largo del cromosoma</option>
                    <option value="Isochromosoma del brazo largo del X">Isochromosoma del brazo largo del X</option>
                    <option value="Cromosoma en anillo del X">Cromosoma en anillo del X</option>
                    <option value="Mosaicismo triple">Mosaicismo triple</option>
                    <option value="Mosaicismo complejo">Mosaicismo complejo</option>
                    <option value="Translocación entre dos cromosomas X">Translocación entre dos cromosomas X</option>
                    <option value="Translocación entre X y un autosoma">Translocación entre X y un autosoma</option>
                    <option value="Translocación entre X y Y">Translocación entre X y Y</option>
                    <option value="Duplicación parcial del X">Duplicación parcial del X</option>
                    <option value="Inversión del cromosoma">Inversión del cromosoma</option>
                    <option value="Cromosoma X dicéntrico">Cromosoma X dicéntrico</option>
                    <option value="Cromosoma en anillo derivado del Y">Cromosoma en anillo derivado del Y</option>
                    <option value="Monosomía X parcial">Monosomía X parcial</option>
                  </select>
                  {errors.tipoSindrome && <span className="error-message">{errors.tipoSindrome}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Cariotipo (*)</label>
                  <input type="text" name="cariotipo" value={formData.cariotipo} onChange={handleInputChange} className={errors.cariotipo ? 'error' : ''} />
                  {errors.cariotipo && <span className="error-message">{errors.cariotipo}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>¿Tiene carnet de discapacidad? (*)</label>
                  <select name="discapacidad" value={formData.discapacidad} onChange={handleInputChange} className={errors.discapacidad ? 'error' : ''}>
                    <option value="">Seleccione una opción...</option>
                    <option value="Si">Si</option>
                    <option value="No">No</option>
                  </select>
                  {errors.discapacidad && <span className="error-message">{errors.discapacidad}</span>}
                </div>
                <div className="form-group">
                  <label>Porcentaje de discapacidad (*)</label>
                  <input type="number" name="porcentaje" value={formData.porcentaje} onChange={handleInputChange} min="0" max="100" step="0.01" className={errors.porcentaje ? 'error' : ''} />
                  {errors.porcentaje && <span className="error-message">{errors.porcentaje}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de discapacidad (*)</label>
                  <select name="tipoDiscapacidad" value={formData.tipoDiscapacidad} onChange={handleInputChange} className={errors.tipoDiscapacidad ? 'error' : ''}>
                    <option value="">Seleccione un tipo...</option>
                    <option value="Física">Física</option>
                    <option value="Auditiva">Auditiva</option>
                    <option value="Lenguaje">Lenguaje</option>
                    <option value="Intelectual">Intelectual</option>
                    <option value="Psicosocial">Psicosocial</option>
                    <option value="Múltiple">Múltiple</option>
                  </select>
                  {errors.tipoDiscapacidad && <span className="error-message">{errors.tipoDiscapacidad}</span>}
                </div>
                <div className="form-group">
                  <label>Grado (*)</label>
                  <select name="grado" value={formData.grado} onChange={handleInputChange} className={errors.grado ? 'error' : ''}>
                    <option value="">Seleccione un grado...</option>
                    {allowedGrados.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  {errors.grado && <span className="error-message">{errors.grado}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>IESS (*)</label>
                  <select name="iess" value={formData.iess} onChange={handleInputChange} className={errors.iess ? 'error' : ''}>
                    <option value="">Seleccione una opción...</option>
                    <option value="Si">Si</option>
                    <option value="No">No</option>
                  </select>
                  {errors.iess && <span className="error-message">{errors.iess}</span>}
                </div>
                <div className="form-group">
                  <label>Edad de detección (*)</label>
                  <input type="text" name="edadDeteccion" value={formData.edadDeteccion} onChange={handleInputChange} className={errors.edadDeteccion ? 'error' : ''} />
                  {errors.edadDeteccion && <span className="error-message">{errors.edadDeteccion}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Talla</label>
                  <input type="text" name="talla" value={formData.talla} onChange={handleInputChange} className={errors.talla ? 'error' : ''} />
                  {errors.talla && <span className="error-message">{errors.talla}</span>}
                </div>
                <div className="form-group">
                  <label>Hormona de crecimiento</label>
                  <select name="hormonaCrecimiento" value={formData.hormonaCrecimiento} onChange={handleInputChange} className={errors.hormonaCrecimiento ? 'error' : ''}>
                    <option value="">Seleccione una opción...</option>
                    <option value="Si">Si</option>
                    <option value="No">No</option>
                  </select>
                  {errors.hormonaCrecimiento && <span className="error-message">{errors.hormonaCrecimiento}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Ministerio de salud (*)</label>
                  <select name="ministerioSalud" value={formData.ministerioSalud} onChange={handleInputChange} className={errors.ministerioSalud ? 'error' : ''}>
                    <option value="">Seleccione una opción...</option>
                    <option value="Si">Si</option>
                    <option value="No">No</option>
                  </select>
                  {errors.ministerioSalud && <span className="error-message">{errors.ministerioSalud}</span>}
                </div>
                <div className="form-group">
                  <label>Mentor {isMentor && <span style={{color: '#666', fontSize: '0.9em'}}>(Bloqueado - Esta socia es mentora)</span>}</label>
                  <select 
                    name="mentor" 
                    value={formData.mentor} 
                    onChange={handleInputChange} 
                    className={errors.mentor ? 'error' : ''} 
                    disabled={loadingMentores || isMentor}
                    style={isMentor ? {backgroundColor: '#f5f5f5', cursor: 'not-allowed'} : {}}
                  >
                    <option value="">{loadingMentores ? 'Cargando mentores...' : isMentor ? 'No aplica - Esta socia es mentora' : 'Seleccione un mentor...'}</option>
                    {mentores.map(m => (
                      <option key={m.mentorId} value={m.mentorId}>
                        {m.mentorNombre} {m.mentorSector ? `- ${m.mentorSector}` : ''}
                      </option>
                    ))}
                  </select>
                  {errors.mentor && <span className="error-message">{errors.mentor}</span>}
                </div>
              </div>
              <div className="form-row single-field">
                <div className="form-group">
                  <label>Estado (*)</label>
                  <select name="estado" value={formData.estado} onChange={handleInputChange} className={errors.estado ? 'error' : ''}>
                    <option value="">Seleccione un estado...</option>
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                  </select>
                  {errors.estado && <span className="error-message">{errors.estado}</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleCancel} className="cancel-button">Cancelar</button>
          <button type="submit" className="create-button">GUARDAR CAMBIOS</button>
        </div>
      </form>
    </div>
  );
}
